import {
  Box,
  Card,
  Dialog,
  Flex,
  FormControlSizes,
  FormGroup,
  HStack,
  IconButton,
  IconName,
  InlineFormattedNumber,
  MixpanelEvent,
  NotificationVariants,
  PriceInput,
  request,
  resolvePricingModeDisplaySpec,
  Stack,
  Text,
  toBigWithDefault,
  Tooltip,
  useCallbackRef,
  useEndpointsContext,
  useGlobalToasts,
  useMixpanel,
  useSecurity,
  type CustomerQuote,
  type Quote,
  type RequiredProperties,
  type UseDisclosureWithContextReturn,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';
import { InitialValueText } from './InitialValueText';
import { ModifyFixingBlotter } from './ModifyFixingBlotter';
import {
  canModifyFixing,
  hasModifyFixingChanges,
  modifyFixingSliceActions,
  type ModifyFixingEntry,
} from './modifyFixingSlice';
import {
  buildUpdateFixingPriceBody,
  canModifyTopLevelFixingEconomics,
  type UpdateFixingPriceBody,
} from './modifyFixingUtils';
import { useModifyFixingState } from './useModifyFixingState';
import { useTopLevelComputedValues } from './useTopLevelComputedValues';

export interface ModifyFixingV2Props {
  mode: 'maker' | 'taker';
  quote: Quote | CustomerQuote;
  orderID: string;
}

type ModifyFixingDialogV2Props = UseDisclosureWithContextReturn<ModifyFixingV2Props>;

export const ModifyFixingDialogV2 = ({ context, ...props }: ModifyFixingDialogV2Props) => {
  if (!context) {
    return null;
  }

  return <ModifyFixingDialogV2Content context={context} key={props.openId} {...props} />;
};

const ModifyFixingDialogV2Content = ({
  context,
  ...dialogProps
}: RequiredProperties<ModifyFixingDialogV2Props, 'context'>) => {
  const { quote, orderID, mode } = context;
  const mixpanel = useMixpanel();
  const { orgApiEndpoint } = useEndpointsContext();
  const { add: addToast } = useGlobalToasts();
  const { state, dispatch } = useModifyFixingState(quote);
  const [loading, setLoading] = useState(false);

  const handleTopLevelPriceChange = useCallbackRef((value: string | undefined) => {
    dispatch(modifyFixingSliceActions.setTopLevelPrice(value));
  });

  const handleTopLevelAmountChange = useCallbackRef((value: string | undefined) => {
    dispatch(modifyFixingSliceActions.setTopLevelAmount(value));
  });

  const handleEntryFixingChange = useCallbackRef((symbol: string, value: string | undefined) => {
    dispatch(modifyFixingSliceActions.setEntryFixing({ symbol, value }));
  });

  const handleEntryPriceChange = useCallbackRef((symbol: string, value: string | undefined) => {
    dispatch(modifyFixingSliceActions.setEntryPrice({ symbol, value }));
  });

  const handleEntryAmountChange = useCallbackRef((symbol: string, value: string | undefined) => {
    dispatch(modifyFixingSliceActions.setEntryAmount({ symbol, value }));
  });

  const canModifyFixingResult = useMemo(() => canModifyFixing(state), [state]);
  const confirmDisabled = loading || !hasModifyFixingChanges(state) || !canModifyFixingResult.valid;

  const handleConfirm = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.ModifyFixing);

    if (!orgApiEndpoint || !state.topLevelPriceField.value || !state.topLevelAmountField.value) {
      addToast({
        variant: NotificationVariants.Negative,
        text: 'Something went wrong when attempting to modify fixing. Please try again, or contact support.',
      });
      return;
    }

    const url =
      mode === 'taker'
        ? `${orgApiEndpoint}/orders/${orderID}/update-fixing-price`
        : `${orgApiEndpoint}/customer/orders/${orderID}/update-fixing-price`;

    let body: UpdateFixingPriceBody;
    try {
      body = buildUpdateFixingPriceBody({ quote, state });
    } catch (e: unknown) {
      const reason = e instanceof Error ? e.message : 'unknown reason';
      addToast({
        variant: NotificationVariants.Negative,
        text: `Failed to create modification request params: ${reason}`,
      });
      return;
    }

    setLoading(true);

    request('PATCH', url, body)
      .then(() => {
        addToast({
          text: 'Successfully modified fixing details.',
          variant: NotificationVariants.Positive,
        });
      })
      .catch((e: ErrorEvent) => {
        addToast({
          text: `Failed to modify fixing details: ${e.message}`,
          variant: NotificationVariants.Negative,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const shouldModifyTopLevel = canModifyTopLevelFixingEconomics(quote);

  return (
    <Dialog
      {...dialogProps}
      confirmLabel="Modify"
      cancelLabel="Close"
      onConfirm={handleConfirm}
      title="Modify Fixing Details"
      width="min(1200px, 90vw)"
      confirmDisabled={confirmDisabled}
      confirmLoading={loading}
      confirmButtonTooltip={canModifyFixingResult.valid === false ? canModifyFixingResult.reason : undefined}
      onConfirmMouseOver={() => dispatch(modifyFixingSliceActions.touchAll())}
      data-testid="modify-fixing-dialog-v2"
      showClose
    >
      <Flex flexDirection="column" gap="spacing12">
        {shouldModifyTopLevel && (
          <TopLevelModificationCard
            entries={state.entries}
            topLevelPriceField={state.topLevelPriceField}
            topLevelPrice={state.topLevelPrice}
            topLevelPricingReference={state.topLevelPricingReference}
            onTopLevelPriceChange={handleTopLevelPriceChange}
            topLevelAmountField={state.topLevelAmountField}
            topLevelAmount={state.topLevelAmount}
            onTopLevelAmountChange={handleTopLevelAmountChange}
            quote={quote}
          />
        )}

        <Box w="100%" h="400px">
          <ModifyFixingBlotter
            entries={state.entries}
            onEntryFixingChange={handleEntryFixingChange}
            onEntryPriceChange={handleEntryPriceChange}
            onEntryAmountChange={handleEntryAmountChange}
          />
        </Box>
      </Flex>
    </Dialog>
  );
};

const TopLevelModificationCard = ({
  entries,
  topLevelPriceField,
  topLevelPrice,
  topLevelPricingReference,
  onTopLevelPriceChange,
  topLevelAmountField,
  topLevelAmount,
  onTopLevelAmountChange,
  quote,
}: {
  entries: ModifyFixingEntry[];
  topLevelPriceField: ModifyFixingEntry['priceField'];
  topLevelPrice?: string;
  topLevelPricingReference?: string;
  onTopLevelPriceChange: (value: string | undefined) => void;
  topLevelAmountField: ModifyFixingEntry['amountField'];
  topLevelAmount?: string;
  onTopLevelAmountChange: (value: string | undefined) => void;
  quote: Quote | CustomerQuote;
}) => {
  const security = useSecurity(quote.Symbol);
  const { priceCurrency, priceDisplaySpec, priceIncrement, toDisplayPrice } = useMemo(
    () =>
      resolvePricingModeDisplaySpec({
        pricingMode: quote.PricingMode,
        security,
      }),
    [quote.PricingMode, security]
  );

  const { computedPrice, summedLegTotal } = useTopLevelComputedValues({
    entries,
    topLevelAmount: topLevelAmountField.value,
    quote,
  });

  const priceIsOff = computedPrice ? !topLevelPriceField.bigValue?.eq(computedPrice) : false;
  const amountIsOff = summedLegTotal ? !topLevelAmountField.bigValue?.eq(summedLegTotal) : false;

  const priceWarning =
    priceIsOff && computedPrice ? (
      <>
        Differs from total / quantity <InlineFormattedNumber number={computedPrice} currency={quote.AmountCurrency} />
      </>
    ) : undefined;

  const amountWarning =
    amountIsOff && summedLegTotal ? (
      <>
        Differs from sum of leg totals <InlineFormattedNumber number={summedLegTotal} currency={quote.AmountCurrency} />
      </>
    ) : undefined;
  const showInitialTopLevelPrice = !toBigWithDefault(topLevelPriceField.value, 0).eq(
    toBigWithDefault(topLevelPrice, 0)
  );
  const showInitialTopLevelAmount = !toBigWithDefault(topLevelAmountField.value, 0).eq(
    toBigWithDefault(topLevelAmount, 0)
  );

  return (
    <Card>
      <HStack justifyContent="space-between">
        <Text color="colorTextSubtle" whiteSpace="nowrap">
          Top-level values
        </Text>

        <HStack
          gap="spacing16"
          alignItems="flex-start"
          w="100%"
          justifyContent="flex-end"
          data-testid="modify-fixing-dialog-top-level-editing"
        >
          <Stack gap="spacing10" w="100px" textAlign="right">
            <Text fontSize="fontSizeSm" color="colorTextDefault">
              Quote
            </Text>
            <Box w="100%">
              <InlineFormattedNumber
                number={toDisplayPrice(topLevelPricingReference)}
                currency={priceCurrency}
                specification={priceDisplaySpec}
                increment={priceIncrement}
                truncate={false}
                align="right"
              />
            </Box>
          </Stack>

          <FormGroup
            label="Price"
            flex={1}
            maxWidth="250px"
            labelSuffix={
              showInitialTopLevelPrice ? (
                <InitialValueText value={topLevelPrice} currency={quote.AmountCurrency} />
              ) : undefined
            }
            error={
              topLevelPriceField.errorString ? (
                <Text data-testid="modify-fixing-top-level-price-warning">{topLevelPriceField.errorString}</Text>
              ) : undefined
            }
            warning={
              !topLevelPriceField.errorString && priceWarning ? (
                <Text data-testid="modify-fixing-top-level-price-warning">{priceWarning}</Text>
              ) : undefined
            }
          >
            <PriceInput
              value={topLevelPriceField.displayValue}
              onChange={onTopLevelPriceChange}
              width="100%"
              data-testid="modify-fixing-top-level-price-input"
              invalid={topLevelPriceField.hasError}
              currency={quote.AmountCurrency}
              prefix={
                priceIsOff && computedPrice ? (
                  <Tooltip tooltip="Set to total / quantity">
                    <IconButton
                      ghost
                      size={FormControlSizes.Small}
                      onClick={() => onTopLevelPriceChange(computedPrice.toFixed())}
                      icon={IconName.Refresh}
                      data-testid="modify-fixing-top-level-price-refresh-button"
                      tabIndex={-1}
                    />
                  </Tooltip>
                ) : undefined
              }
            />
          </FormGroup>

          <Stack gap="spacing10" w="100px" textAlign="right">
            <Text fontSize="fontSizeSm" color="colorTextDefault">
              Quantity
            </Text>
            <Box w="100%">
              <InlineFormattedNumber
                number={quote.TradedQty}
                currency={quote.Currency}
                truncate={false}
                align="right"
              />
            </Box>
          </Stack>

          <FormGroup
            label="Total"
            flex={1}
            maxWidth="250px"
            labelSuffix={
              showInitialTopLevelAmount ? (
                <InitialValueText value={topLevelAmount} currency={quote.AmountCurrency} />
              ) : undefined
            }
            error={
              topLevelAmountField.errorString ? (
                <Text data-testid="modify-fixing-top-level-amount-warning">{topLevelAmountField.errorString}</Text>
              ) : undefined
            }
            warning={
              !topLevelAmountField.errorString && amountWarning ? (
                <Text data-testid="modify-fixing-top-level-amount-warning">{amountWarning}</Text>
              ) : undefined
            }
          >
            <PriceInput
              value={topLevelAmountField.displayValue}
              onChange={onTopLevelAmountChange}
              width="100%"
              data-testid="modify-fixing-top-level-amount-input"
              invalid={topLevelAmountField.hasError}
              currency={quote.AmountCurrency}
              prefix={
                amountIsOff && summedLegTotal ? (
                  <Tooltip tooltip="Set to sum of leg totals">
                    <IconButton
                      ghost
                      size={FormControlSizes.Small}
                      onClick={() => onTopLevelAmountChange(summedLegTotal.toFixed())}
                      icon={IconName.Refresh}
                      data-testid="modify-fixing-top-level-amount-refresh-button"
                      tabIndex={-1}
                    />
                  </Tooltip>
                ) : undefined
              }
            />
          </FormGroup>
        </HStack>
      </HStack>
    </Card>
  );
};
